import React from 'react';
import { User, Mail, Phone, Gift, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const THERAPISTS = [
  { id: 'no_preference', name: 'Any Available', role: 'First available therapist', image: null, specialties: [] },
  { id: 'michael', name: 'Dr. Michael', role: 'Senior Therapist', image: 'https://customer-assets.emergentagent.com/job_905f2ba3-e3d2-46ba-8c1f-ae54c31b9b64/artifacts/c0i7hb50_20251128_115503.jpg', specialties: ['Deep Tissue', 'Sports', 'Thai'] },
  { id: 'ana', name: 'Ana', role: 'Senior Therapist', image: 'https://customer-assets.emergentagent.com/job_905f2ba3-e3d2-46ba-8c1f-ae54c31b9b64/artifacts/a2rw88g0_Screenshot_20260113_221311_Instagram.jpg', specialties: ['Lomi Lomi', 'Deep Tissue', 'Shiatsu'] },
  { id: 'buggie', name: 'Buggie', role: 'Senior Deep Tissue Specialist', image: 'https://static.prod-images.emergentagent.com/jobs/4d2f52c7-22cd-4150-9059-ad99d56ca7fb/images/2a2dde51ef093902321b2cd395ef519878ab8ec4aa4bb42041544680255eb1cc.jpeg', specialties: ['Deep Tissue', 'Shiatsu', 'Thai'] },
  { id: 'leslie', name: 'Leslie', role: 'Senior Relaxation Specialist', image: 'https://static.prod-images.emergentagent.com/jobs/4d2f52c7-22cd-4150-9059-ad99d56ca7fb/images/6ee6b03adebc10c2697f50e53e6b912b6d900ba9135e9a05b428dc2a75f8a5dd.jpeg', specialties: ['Relaxing', 'Swedish', 'Deep Tissue'] },
  { id: 'victor', name: 'Victor', role: 'Senior Deep Tissue & Relaxing', image: 'https://images.unsplash.com/photo-1650044252595-cacd425982ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtYWxlJTIwbWFzc2FnZSUyMHRoZXJhcGlzdCUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3Mjk3ODg0NXww&ixlib=rb-4.1.0&q=85', specialties: ['Deep Tissue', 'Relaxing', 'Sports'] },
  { id: 'eegi', name: 'Eegi', role: 'Senior Thai & Deep Tissue Specialist', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/wzere5yf_IMG-20260810-WA0021.webp', specialties: ['Thai', 'Shiatsu', 'Deep Tissue', 'Relaxing'] },
  { id: 'lana', name: 'Lana', role: 'Senior Swedish & Relaxation Specialist', image: 'https://customer-assets.emergentagent.com/job_massage-therapy-19/artifacts/jgespj5n_Screenshot_20260122_103240_WhatsApp.jpg', specialties: ['Swedish', 'Relaxing', 'Stress Relief'] },
  { id: 'mayara', name: 'Mayara', role: 'Senior Deep Tissue & Relaxing', image: null, specialties: ['Deep Tissue', 'Relaxing', 'Swedish'] },
  { id: 'rino', name: 'Rino', role: 'Senior Massage Therapist', image: 'https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/u5qabor3_IMG-20260713-WA0002.jpg', specialties: ['Deep Tissue', 'Sports', 'Thai', 'Shiatsu', 'Hot Stone'] },
  { id: 'salome', name: 'Salome', role: 'Senior Relaxing Massage Therapist', image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/crulhcud_IMG-20260809-WA0008.webp', specialties: ['Relaxing', 'Swedish', 'Personalized Care'] },
];

// Clarehall-only therapists
const CLAREHALL_THERAPIST_IDS = ['michael', 'ana', 'no_preference'];

// Specialty-based therapist recommendations
const RECOMMENDED_BY_KEYWORD = [
  { keyword: 'lomi lomi', ids: ['michael', 'ana'], label: 'Lomi Lomi' },
  { keyword: 'shiatsu', ids: ['buggie', 'eegi', 'ana'], label: 'Shiatsu' },
];

const BookingStepDetails = ({ formData, setFormData, setStep, loyaltyInfo }) => {
  const isCouples = formData.service?.name?.toLowerCase().includes('couple');
  const isClarehall = formData.location === 'clarehall';
  const serviceName = formData.service?.name?.toLowerCase() || '';
  const recommendation = RECOMMENDED_BY_KEYWORD.find((r) => serviceName.includes(r.keyword));
  const baseTherapists = isClarehall
    ? THERAPISTS.filter(t => CLAREHALL_THERAPIST_IDS.includes(t.id))
    : THERAPISTS;
  const recommendedIds = recommendation
    ? recommendation.ids.filter((id) => baseTherapists.some((t) => t.id === id))
    : [];
  const availableTherapists = recommendedIds.length
    ? [
        ...baseTherapists.filter((t) => t.id === 'no_preference'),
        ...baseTherapists.filter((t) => recommendedIds.includes(t.id)),
        ...baseTherapists.filter((t) => t.id !== 'no_preference' && !recommendedIds.includes(t.id)),
      ]
    : baseTherapists;

  return (
  <div className="space-y-4">
    <h3 className="text-base sm:text-lg font-semibold text-stone-800">Your Details</h3>
    
    <div className="space-y-3">
      <div>
        <label className="block text-xs sm:text-sm font-medium text-stone-600 mb-1">Full Name *</label>
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={formData.client_name}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            className="pl-9 text-sm h-10"
            placeholder="Your full name"
            required
            data-testid="booking-name-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-stone-600 mb-1">Email *</label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            type="email"
            value={formData.client_email}
            onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
            className="pl-9 text-sm h-10"
            placeholder="your@email.com"
            required
            data-testid="booking-email-input"
          />
        </div>
        {loyaltyInfo && loyaltyInfo.total_visits > 0 && (
          <div className="mt-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3" data-testid="returning-customer-banner">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  Welcome back{loyaltyInfo.customer_name ? `, ${loyaltyInfo.customer_name.split(' ')[0]}` : ''}!
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  You&apos;ve visited us {loyaltyInfo.total_visits} time{loyaltyInfo.total_visits !== 1 ? 's' : ''}
                  {loyaltyInfo.loyalty_tier && (
                    <span className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      loyaltyInfo.loyalty_tier === 'Gold' ? 'bg-yellow-200 text-yellow-800' :
                      loyaltyInfo.loyalty_tier === 'Silver' ? 'bg-gray-200 text-gray-700' :
                      'bg-orange-200 text-orange-800'
                    }`} data-testid="loyalty-tier-badge">
                      {loyaltyInfo.loyalty_tier}
                    </span>
                  )}
                </p>
                {loyaltyInfo.current_discount_percent > 0 && (
                  <p className="text-xs text-emerald-700 font-medium mt-1">
                    {loyaltyInfo.current_discount_percent}% loyalty discount applied at checkout
                  </p>
                )}
                {loyaltyInfo.next_discount && (
                  <p className="text-[11px] text-amber-600 mt-0.5">
                    {loyaltyInfo.next_discount.visits_needed} more visit{loyaltyInfo.next_discount.visits_needed !== 1 ? 's' : ''} to unlock {loyaltyInfo.next_discount.discount} off
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {loyaltyInfo && loyaltyInfo.total_visits === 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-sky-600 bg-sky-50 px-3 py-2 rounded-lg" data-testid="new-customer-welcome">
            <Gift size={14} />
            <span>Welcome! First-time visitors enjoy special discounts</span>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-stone-600 mb-1">Phone *</label>
        <div className="relative">
          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            type="tel"
            value={formData.client_phone}
            onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
            className="pl-9 text-sm h-10"
            placeholder="+353 85 XXX XXXX"
            required
            data-testid="booking-phone-input"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-stone-600 mb-2">
          {formData.service?.name?.toLowerCase().includes('couple') ? 'Choose Therapist for Person 1' : 'Choose Your Therapist'}
        </label>
        {recommendation && recommendedIds.length > 0 && (
          <div className="mb-2 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg" data-testid="therapist-recommendation-note">
            <Sparkles size={13} className="flex-shrink-0" />
            <span>
              Best for {recommendation.label}:{' '}
              <strong>{recommendedIds.map((id) => THERAPISTS.find((t) => t.id === id)?.name).join(', ')}</strong>
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
          {availableTherapists.map((therapist) => (
            <button
              key={therapist.id}
              onClick={() => setFormData({ ...formData, therapist_preference: therapist.id })}
              className={`relative p-2 rounded-xl border-2 text-left transition-all flex items-center gap-2 ${
                formData.therapist_preference === therapist.id
                  ? 'border-luxury-green bg-sage-50'
                  : recommendedIds.includes(therapist.id)
                    ? 'border-amber-300 hover:border-amber-400'
                    : 'border-stone-200 hover:border-sage-300'
              }`}
              data-testid={`therapist-${therapist.id}`}
            >
              {recommendedIds.includes(therapist.id) && (
                <span className="absolute -top-2 right-2 bg-amber-500 text-white text-[9px] font-semibold px-2 py-0.5 rounded-full" data-testid={`recommended-badge-${therapist.id}`}>
                  Recommended
                </span>
              )}
              {therapist.image ? (
                <img
                  src={therapist.image}
                  alt={therapist.name}
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                  style={therapist.id === 'eegi' ? { objectPosition: 'center 15%' } : {}}
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-luxury-green" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium text-stone-800 truncate">{therapist.name}</p>
                <p className="text-[10px] text-stone-400 truncate">{therapist.role}</p>
              </div>
            </button>
          ))}
        </div>
        {formData.therapist_preference !== 'no_preference' && (
          <div className="mt-2 flex flex-wrap gap-1">
            {THERAPISTS.find(t => t.id === formData.therapist_preference)?.specialties.map(s => (
              <span key={s} className="text-[10px] bg-sage-50 text-sage-700 px-2 py-0.5 rounded-full border border-sage-200">{s}</span>
            ))}
          </div>
        )}
      </div>

      {/* Second therapist picker for Couples */}
      {formData.service?.name?.toLowerCase().includes('couple') && (
        <div>
          <label className="block text-xs sm:text-sm font-medium text-stone-600 mb-2">Choose Therapist for Person 2</label>
          <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
            {availableTherapists.map((therapist) => (
              <button
                key={therapist.id}
                onClick={() => setFormData({ ...formData, therapist_preference_2: therapist.id })}
                className={`p-2 rounded-xl border-2 text-left transition-all flex items-center gap-2 ${
                  formData.therapist_preference_2 === therapist.id
                    ? 'border-rose-400 bg-rose-50'
                    : 'border-stone-200 hover:border-rose-300'
                }`}
                data-testid={`therapist2-${therapist.id}`}
              >
                {therapist.image ? (
                  <img
                    src={therapist.image}
                    alt={therapist.name}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    style={therapist.id === 'eegi' ? { objectPosition: 'center 15%' } : {}}
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <User size={16} className="text-rose-500" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium text-stone-800 truncate">{therapist.name}</p>
                  <p className="text-[10px] text-stone-400 truncate">{therapist.role}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-stone-400 italic leading-relaxed" data-testid="therapist-matching-note">
        We will try our best to match you with your desired therapist. In the event that this is not possible, we will let you know of a suitable replacement.
      </p>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-stone-600 mb-1">Special Requests</label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="text-sm"
          placeholder="Any specific needs..."
          rows={2}
        />
      </div>
    </div>

    <Button 
      onClick={() => setStep(5)} 
      disabled={!formData.client_name || !formData.client_email || !formData.client_phone}
      className="w-full bg-luxury-green hover:bg-luxury-green/90 text-white rounded-full disabled:opacity-50 text-sm h-10"
      data-testid="booking-review-btn"
    >
      Review Booking
    </Button>
  </div>
  );
};

export default BookingStepDetails;
